/**
 * 占位头像的配色（21h，旧版 `widgets/gradient_avatar.dart` 同口径）。
 *
 * 新版此前所有占位头像都是同一个死灰 `--color-avatar-bg: #c8c9cc`（人工反馈
 * 「死气沉沉」）。旧版是 **8 组渐变按名字 hash 取色**，这里逐条照搬：
 * - 色表与顺序 = `GradientAvatar.gradients`（warm/yellow/green/teal/blue/
 *   indigo/pink/rose）；
 * - 索引 = `hash = hash * 31 + codeUnitAt(i)` 累加后 `& 0xFFFFFFFF`，再 `% 8`。
 *   **必须用 UTF-16 码元逐个累加**（Dart 的 `codeUnitAt` 语义），不能按码点——
 *   否则 emoji / 生僻字的名字会算出不同的颜色，与旧版对不上；
 * - 渐变方向 135°（Flutter 的 topLeft→bottomRight）；
 * - **emoji 名例外**：不上渐变，`#F7F7F8` 平底 + 黑字（旧版 `_isEmoji` 分支）。
 *
 * 用法：调用方把 `avatarStyle(name)` 展开到头像元素的 `style` 上，就地覆盖
 * `--color-avatar-bg` / `--color-avatar-fg` 两个变量——**各处头像的 SCSS 不用改结构**
 * （13 处都是 `background: var(--color-avatar-bg)`，简写能接渐变值）。
 */

/** 8 组渐变，顺序与旧版 `GradientAvatar.gradients` 严格一致（索引即色号） */
export const AVATAR_GRADIENTS: ReadonlyArray<readonly [from: string, to: string]> = [
  ['#ff885e', '#ff516a'], // warm
  ['#ffcd6a', '#ffa85c'], // yellow
  ['#a0de7e', '#54cb68'], // green
  ['#53edd6', '#28c9b7'], // teal
  ['#72d5fd', '#2a9ef1'], // blue
  ['#82b1ff', '#665fff'], // indigo
  ['#e0a2f3', '#d669ed'], // pink
  ['#ffa8a8', '#f8739f'], // rose
];

/** emoji 名的平底与字色（旧版 `isEmoji ? Color(0xFFF7F7F8) : null` + 黑字） */
export const AVATAR_EMOJI_BG = '#f7f7f8';
const AVATAR_EMOJI_FG = '#000';
const AVATAR_FG = '#fff';

/**
 * 名字 → 渐变索引。旧版 `_getGradientIndex` 逐字搬：
 * `hash = (hash * 31 + codeUnit) & 0xFFFFFFFF`。
 * JS 的 `*` 在大数上会丢精度，所以用 `Math.imul` 做 32 位乘法再用
 * `>>> 0` 取无符号 32 位——与 Dart 的 `& 0xFFFFFFFF` 等价。
 */
export function avatarGradientIndex(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (Math.imul(hash, 31) + name.charCodeAt(i)) >>> 0;
  }
  return hash % AVATAR_GRADIENTS.length;
}

/** 首字符是 emoji（旧版 `_isEmoji`：首码点落在 U+1F300~U+1F9FF） */
export function isEmojiName(name: string): boolean {
  if (name === '') return false;
  const first = name.codePointAt(0);
  return first !== undefined && first >= 0x1f300 && first <= 0x1f9ff;
}

/** 头像底色的 CSS 值（渐变或 emoji 平底）。空名回落首个渐变，不留灰 */
export function avatarBackground(name: string): string {
  if (isEmojiName(name)) return AVATAR_EMOJI_BG;
  const [from, to] = AVATAR_GRADIENTS[avatarGradientIndex(name)]!;
  return `linear-gradient(135deg, ${from}, ${to})`;
}

/**
 * 展开到头像元素 `style` 上的内联自定义属性。
 * 调用方：`<span className={styles.avatar} style={avatarStyle(name)}>`。
 */
export function avatarStyle(name: string): Record<string, string> {
  return {
    '--color-avatar-bg': avatarBackground(name),
    '--color-avatar-fg': isEmojiName(name) ? AVATAR_EMOJI_FG : AVATAR_FG,
  };
}
